/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Turns the flat list from buildSidenavRoutes() -- the same list the classic
 * Sidenav renders, already filtered by entitlements, RBAC, travel gating and
 * the user's saved layout -- into sections for the v2 sidebar. Nothing is
 * added or re-checked here: v2 shows exactly the items classic shows.
 */

const TOP_SECTION = "__top";

/** A link item, flattening nested parents (Travel, Call Center) into groups. */
function toItem(route) {
  const children = Array.isArray(route.collapse)
    ? route.collapse.filter((c) => !c.hideInSidenav && c.route).map(toItem)
    : [];
  return {
    key: route.key,
    name: route.name,
    icon: route.icon,
    route: route.route || null,
    href: route.href || null,
    children,
  };
}

export function buildSidebarSections(sidenavRoutes) {
  const sections = [];
  let current = { key: TOP_SECTION, title: null, items: [] };

  (sidenavRoutes || []).forEach((route) => {
    if (!route || route.hideInSidenav) return;
    if (route.type === "title") {
      if (current.items.length || current.key !== TOP_SECTION) sections.push(current);
      current = { key: route.key, title: route.title || route.name, items: [] };
      return;
    }
    if (route.type === "collapse") {
      const item = toItem(route);
      if (item.route || item.href || item.children.length) current.items.push(item);
    }
  });
  if (current.items.length || current.key !== TOP_SECTION) sections.push(current);
  return sections.filter((s) => s.items.length > 0);
}

/** The route key of the home dashboard (the one pinned per user, /dashboard). */
export const HOME_KEY = "dashboard";

/**
 * Lifts the home dashboard out of its section so the sidebar can show it as
 * a universal Home link above every app's sections. It is only returned when
 * the user's sidebar already has it, so RBAC and saved layouts still decide.
 */
export function splitHome(sections) {
  let home = null;
  const rest = sections
    .map((s) => ({
      ...s,
      items: s.items.filter((item) => {
        if (item.key !== HOME_KEY) return true;
        home = { ...item, name: "Home", icon: "home" };
        return false;
      }),
    }))
    .filter((s) => s.items.length > 0);
  return { home, sections: rest };
}

/** Every navigable item, children included, for favourites and active lookup. */
export function flattenItems(sections) {
  const out = [];
  sections.forEach((s) =>
    s.items.forEach((item) => {
      if (item.route || item.href) out.push(item);
      item.children.forEach((c) => out.push(c));
    })
  );
  return out;
}

/** The item whose route is the longest prefix of the current path. */
export function activeItemKey(items, pathname) {
  let best = null;
  items.forEach((item) => {
    if (!item.route) return;
    const r = item.route.replace(/\/$/, "");
    if (pathname === r || pathname.startsWith(`${r}/`)) {
      if (!best || r.length > best.route.length) best = { key: item.key, route: r };
    }
  });
  return best?.key || null;
}

/**
 * The sidebar's "Jump to" filter. Keeps the items whose name contains every
 * word of the query (any order, any case). A group whose own name matches
 * keeps all its children; otherwise it keeps only the children that match.
 * A section whose title matches keeps all its items. Empty sections drop out.
 * Only narrows what the user can already see; it never adds an item.
 */
export function filterSections(sections, query) {
  const words = String(query || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return sections;
  const hits = (text) => {
    const t = String(text || "").toLowerCase();
    return words.every((w) => t.includes(w));
  };
  return sections
    .map((section) => {
      if (hits(section.title)) return section;
      const items = section.items
        .map((item) => {
          if (hits(item.name)) return item;
          const children = item.children.filter((c) => hits(c.name) || hits(`${item.name} ${c.name}`));
          return children.length ? { ...item, children } : null;
        })
        .filter(Boolean);
      return { ...section, items };
    })
    .filter((s) => s.items.length > 0);
}
